// components/QuoteButton.jsx
import React, { useState, useEffect } from "react";
import { FileText, Loader, ExternalLink, CheckCircle } from "lucide-react";
import apiService from "../services/api";
import QuoteAuthModal, { quoteAuth } from "./QuoteAuthModal";
import { useTheme } from "../contexts/ThemeContext";

const QuoteButton = ({ opportunity }) => {
  const { isDarkMode } = useTheme();
  const [loading, setLoading] = useState(false);
  const [quoteData, setQuoteData] = useState(null);
  const [error, setError] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);

  useEffect(() => {
    if (opportunity.customFields?.quoteSystemId) {
      fetchQuoteData();
    }
  }, [opportunity.id]);

  const fetchQuoteData = async () => {
    try {
      const response = await apiService.getOpportunityQuote(opportunity.id);
      if (response.data) setQuoteData(response.data);
    } catch (err) {
      console.error("Error fetching quote:", err);
    }
  };

  // Called when user clicks "Create Quote"
  const handleInitializeQuote = async () => {
    // Not logged into the Quote system yet — show login modal first
    if (!quoteAuth.isAuthenticated()) {
      setShowAuthModal(true);
      return;
    }
    await createQuote();
  };

  // Separated so it can be called directly after modal login without a second click
  const createQuote = async () => {
    try {
      setLoading(true);
      setError(null);

      const quoteToken = quoteAuth.getToken();

      // Pass the quote token so the CRM backend can forward it to the Quote API
      const response = await apiService.initializeQuote(
        opportunity.id,
        quoteToken,
      );

      if (response.data?.quoteUrl) {
        const width = 1400;
        const height = 900;
        const left = (window.screen.width - width) / 2;
        const top = (window.screen.height - height) / 2;

        window.open(
          response.data.quoteUrl,
          "QuoteSystem",
          `width=${width},height=${height},left=${left},top=${top}`,
        );

        if (!response.data.existing) setQuoteData(response.data);
      }
    } catch (err) {
      console.error("Error initializing quote:", err);

      // Token was rejected by the Quote system — clear it and re-prompt login
      if (err?.response?.status === 401) {
        quoteAuth.clear();
        setShowAuthModal(true);
        return;
      }

      setError("Failed to initialize quote. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleAuthSuccess = () => {
    setShowAuthModal(false);
    createQuote(); // proceed immediately, no second click needed
  };

  const openExistingQuote = () => {
    const quoteUrl =
      quoteData?.quoteUrl ||
      `${process.env.REACT_APP_QUOTE_URL}/new-quote?connectionId=${opportunity.customFields.quoteConnectionId}&opportunityId=${opportunity.id}`;

    const width = 1400;
    const height = 900;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;

    window.open(
      quoteUrl,
      "QuoteSystem",
      `width=${width},height=${height},left=${left},top=${top}`,
    );
  };

  if (quoteData || opportunity.customFields?.quoteSystemId) {
    return (
      <button
        onClick={openExistingQuote}
        className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
          isDarkMode
            ? "bg-green-600 hover:bg-green-700 text-white"
            : "bg-green-500 hover:bg-green-600 text-white"
        }`}
      >
        <CheckCircle size={16} />
        <span>View Quote</span>
        <ExternalLink size={14} />
      </button>
    );
  }

  return (
    <>
      <div>
        <button
          onClick={handleInitializeQuote}
          disabled={loading}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors disabled:opacity-50 ${
            isDarkMode
              ? "bg-blue-600 hover:bg-blue-700 text-white"
              : "bg-blue-500 hover:bg-blue-600 text-white"
          }`}
        >
          {loading ? (
            <>
              <Loader size={16} className="animate-spin" />
              <span>Initializing…</span>
            </>
          ) : (
            <>
              <FileText size={16} />
              <span>Create Quote</span>
            </>
          )}
        </button>

        {error && (
          <p
            className={`text-sm mt-2 ${isDarkMode ? "text-red-400" : "text-red-600"}`}
          >
            {error}
          </p>
        )}
      </div>

      {showAuthModal && (
        <QuoteAuthModal
          onSuccess={handleAuthSuccess}
          onClose={() => setShowAuthModal(false)}
        />
      )}
    </>
  );
};

export default QuoteButton;
